﻿import { openOverlay } from './SearchOverlay'

const windowWidthResizeAutoclose = 991

export const initialise = () => {
    const header = document.querySelector<HTMLElement>('.component-header')
    const burgerMenu = document.querySelector<HTMLDialogElement>('.component-burger-menu')
    if (!header || !burgerMenu) return

    removeTabIndex(header)
    removeTabIndex(burgerMenu)
    assignVisibility(header)
    assignVisibility(burgerMenu)
    headerSticky(header)
    headerNavigationMenus(header)
    burgerToggle(header, burgerMenu)
    burgerNavigationMenus(burgerMenu)
}

const removeTabIndex = (navigation: HTMLElement) => {
    const navigationLinks = [
        ...navigation.querySelectorAll<HTMLElement>('*[tabindex]')
    ]

    navigationLinks.forEach((link) => {
        link.removeAttribute('tabindex')
    })
}

const assignVisibility = (navigation: HTMLElement) => {
    const navLevels = navigation.querySelectorAll<HTMLElement>('.navLevel-1, .navLevel-2')

    navLevels.forEach((navLevel) => {
        navLevel.setAttribute('aria-hidden', 'true')
    })
}

const headerSticky = (header: HTMLElement) => {
    const scrollUpClass: string = 'component-header--scroll-up'
    const scrollDownClass: string = 'component-header--scroll-down'
    let lastScrollPosition: number = 0

    const headerHeight: number = header.offsetHeight

    window.addEventListener('scroll', () => {
        const currentScrollPosition: number = window.scrollY

        if (currentScrollPosition <= headerHeight) {
            header.classList.remove(scrollUpClass, scrollDownClass)
        } else if (currentScrollPosition > lastScrollPosition) {
            header.classList.add(scrollDownClass)
            header.classList.remove(scrollUpClass)
        } else {
            header.classList.add(scrollUpClass)
            header.classList.remove(scrollDownClass)
        }
        lastScrollPosition = currentScrollPosition
    })
}

const openNavigationMenu = (item: HTMLElement, menu: HTMLElement, keyboardNavigation: boolean = false) => {
    if (item.classList.contains('open')) return
    item.classList.add('open')
    item.setAttribute('aria-expanded', 'true')
    menu.setAttribute('aria-hidden', 'false')

    if (!keyboardNavigation) return
    menu.querySelector<HTMLAnchorElement>('a')?.focus()
}

const closeNavigationMenu = (item: HTMLElement, menu: HTMLElement, keyboardNavigation: boolean = false) => {
    const focusedElement = document.activeElement as HTMLElement | null
    if (focusedElement && menu.contains(focusedElement)) {
        focusedElement.blur()
    }

    item.classList.remove('open')
    item.setAttribute('aria-expanded', 'false')
    menu.setAttribute('aria-hidden', 'true')

    const childItems = menu.querySelectorAll<HTMLElement>('.hasChildren')
    childItems.forEach((childItem: HTMLElement) => {
        const childMenu = childItem.querySelector<HTMLElement>('.navList')
        if (!childMenu) return
        closeNavigationMenu(childItem, childMenu, false)
    })

    if (!keyboardNavigation) return
    item.querySelector<HTMLButtonElement>('.icon')?.focus()
}

const toggleNavigationMenu = (item: HTMLElement, menu: HTMLElement, keyboardNavigation: boolean = false) => {
    item.classList.toggle('open')
    item.setAttribute('aria-expanded', item.classList.contains('open') ? 'true' : 'false')
    menu.setAttribute('aria-hidden', item.classList.contains('open') ? 'false' : 'true')

    if (!keyboardNavigation) return

    if (item.classList.contains('open')) {
        menu.querySelector<HTMLAnchorElement>('a')?.focus()
    } else {
        item.querySelector<HTMLButtonElement>('.icon')?.focus()
    }
}

const headerNavigationMenus = (header: HTMLElement) => {
    const items = header.querySelectorAll<HTMLElement>('.mainNav-widget .hasChildren')

    items.forEach((item: HTMLElement) => {
        const icon = item.querySelector<HTMLButtonElement>('.icon')
        const menu = item.querySelector<HTMLElement>('.navList')
        const parent = item.parentElement?.parentElement?.classList.contains('hasChildren') ? item.parentElement?.parentElement : null
        const childrenMenus = item.querySelectorAll<HTMLElement>('.navList .hasChildren')
        if (!menu || !icon) return

        closeNavigationMenu(item, menu)

        icon.addEventListener('focus', (event: MouseEvent) => {
            removeTabIndex(header)
        })

        icon.addEventListener('keydown', (event: KeyboardEvent) => {
            if (event.key === ' ' || event.key === 'Enter') {
                event.preventDefault()
                toggleNavigationMenu(item, menu, true)
            }
        })

        menu.addEventListener('focusout', (event: FocusEvent) => {
            const relatedTarget = event.relatedTarget as HTMLElement | null
            if (relatedTarget && (menu.contains(relatedTarget))) return
            closeNavigationMenu(item, menu, true)
        })

        item.addEventListener('mouseenter', (event: MouseEvent) => {
            openNavigationMenu(item, menu)
        })

        item.addEventListener('mouseleave', (event: MouseEvent) => {
            const relatedTarget = event.relatedTarget as HTMLElement | null
            let isChild = false
            for (const childMenu of childrenMenus) {
                if (relatedTarget && childMenu.contains(relatedTarget)) {
                    isChild = true
                    break
                }
            }
            if (isChild) return

            if (parent && relatedTarget && parent.contains(relatedTarget)) {
                closeNavigationMenu(item, menu)
                return
            }

            if (relatedTarget && menu.contains(relatedTarget)) {
                return
            }

            closeNavigationMenu(item, menu)
        })
    })
}

const burgerOpen = (dialog: HTMLDialogElement, burgerMenuToggles: Array<Element>) => {
    document.body.classList.add('overlay--open')
    dialog.showModal()
    burgerMenuToggles.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'true')
        dialog.setAttribute('aria-hidden', 'false')
    })
}

const burgerClose = (dialog: HTMLDialogElement, burgerMenuToggles: Array<Element>) => {
    document.body.classList.remove('overlay--open')
    dialog.close()
    burgerMenuToggles.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false')
        dialog.setAttribute('aria-hidden', 'true')
    })
}

const burgerToggle = (header: HTMLElement, burgerMenu: HTMLDialogElement) => {
    const burgerMenuToggles = [
        ...header.querySelectorAll('.component-header__action-burger-toggle'),
        ...burgerMenu.querySelectorAll('.component-burger-menu__action-burger-toggle')
    ]

    const dialog: HTMLDialogElement | null = document.querySelector('.component-burger-menu')
    if (!dialog) return
    dialog.setAttribute('aria-hidden', 'true')

    burgerMenuToggles.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false')
        toggle.setAttribute('aria-controls', 'burger-menu')

        toggle.addEventListener('click', () => {
            if (dialog.open) {
                burgerClose(dialog, burgerMenuToggles)
                return
            }

            burgerNavigationMenusClose(dialog)
            burgerOpen(dialog, burgerMenuToggles)
            burgerCloseListener(dialog, burgerMenu, burgerMenuToggles)
            burgerOpenSearchOverlayListener(dialog, burgerMenuToggles)
            burgerResizeAutoCloseListener(dialog, burgerMenuToggles)
        })
    })
}

const burgerNavigationMenusClose = (dialog: HTMLDialogElement) => {
    const menuItems = dialog?.querySelectorAll<HTMLElement>('.hasChildren')
    menuItems.forEach((menuItem: HTMLUListElement) => {
        const menu = menuItem.querySelector<HTMLElement>('.navList')
        if (!menu) return
        closeNavigationMenu(menuItem, menu)
    })
}

const burgerCloseListener = (dialog: HTMLDialogElement, burgerMenu: HTMLDialogElement, burgerMenuToggles: Array<Element>) => {
    dialog.addEventListener('close', function closeDialog() {
        burgerClose(dialog, burgerMenuToggles)

        setTimeout(() => {
            const items = burgerMenu?.querySelectorAll<HTMLElement>('.mainNav-widget .hasChildren')
            items.forEach((item: HTMLUListElement) => {
                const menu = item.querySelector<HTMLElement>('.navList')
                if (!menu) return
                closeNavigationMenu(item, menu)
            })
            dialog.removeEventListener('close', closeDialog)
        }, 300)
    })
}

const burgerOpenSearchOverlayListener = (dialog: HTMLDialogElement, burgerMenuToggles: Array<Element>) => {
    const overlayOpenButton = document.querySelector<HTMLButtonElement>('.component-burger-menu .component-burger-menu__action-search-overlay')
    overlayOpenButton?.addEventListener('click', function searchOverlayListener() {
        burgerClose(dialog, burgerMenuToggles)

        const overlay = document.querySelector<HTMLDialogElement>('.component-search-overlay')
        if (!overlay) return

        requestAnimationFrame(() => openOverlay(overlay))
        overlayOpenButton.removeEventListener('click', searchOverlayListener)
    })
}

const burgerResizeAutoCloseListener = (dialog: HTMLDialogElement, burgerMenuToggles: Array<Element>) => {
    window.addEventListener('resize', function closeDialogResize() {
        if (window.innerWidth >= windowWidthResizeAutoclose) {
            burgerClose(dialog, burgerMenuToggles)
            window.removeEventListener('resize', closeDialogResize)
        }
    })
}

const burgerNavigationMenus = (dialog: HTMLDialogElement) => {
    const items = dialog.querySelectorAll<HTMLElement>('.mainNav-widget .hasChildren')

    items.forEach((item: HTMLUListElement) => {
        const icon = item.querySelector<HTMLButtonElement>('.icon')
        const menu = item.querySelector<HTMLElement>('.navList')
        const parent = item.parentElement?.parentElement?.classList.contains('hasChildren') ? item.parentElement?.parentElement : null
        if (!menu || !icon) return
        const menuBack = menu.querySelector<HTMLButtonElement>('.nav-back-btn')
        if (!menuBack) return

        closeNavigationMenu(item, menu)

        icon.addEventListener('focus', (event: MouseEvent) => {
            removeTabIndex(dialog)
        })

        icon.addEventListener('click', (event: MouseEvent) => {
            if (item.classList.contains('open')) {
                closeNavigationMenu(item, menu, true)
            } else {
                openNavigationMenu(item, menu)
            }
        })

        icon.addEventListener('keydown', (event: KeyboardEvent) => {
            if (event.key === ' ' || event.key === 'Enter') {
                event.preventDefault()
                if (item.classList.contains('open')) {
                    closeNavigationMenu(item, menu, true)
                } else {
                    openNavigationMenu(item, menu, true)
                }
            }
        })

        menuBack.addEventListener('click', () => {
            closeNavigationMenu(item, menu, true)
        })
    })
}